import React, { useMemo, useState, ReactNode } from "react";
import { Dialog, PlainButton } from "@jugl-web/ui-components";
import { Avatar, Button } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import {
  ContactIntelligenceInfoSection,
  ContactInfoData,
} from "../ContactIntelligenceInfoSection";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as MingcuteIcon } from "./assets/mingcute.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as Shadow } from "./assets/shadow.svg";
import platformsImage from "./assets/platforms.png";

export const VisitorsIntelligenceDialog: React.FC<{
  size?: "default" | "small";
}> = ({ size = "default" }) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const whatYouGetItems = useMemo(
    () => [
      {
        title: t({
          id: "contacts-page.track-chat-visitors",
          defaultMessage: "Track Website chat Visitors",
        }),
        description: t({
          id: "contacts-page.track-chat-visitors-desc",
          defaultMessage:
            "Get the names, LinkedIn profiles, and email addresses of real people on your site 🔍",
        }),
      },
      {
        title: t({
          id: "contacts-page.identify-companies",
          defaultMessage: "Identify Companies behind",
        }),
        description: t(
          {
            id: "contacts-page.identify-companies-desc",
            defaultMessage:
              "Get details like company name, industry, size, revenue, and social profiles - <highlightedText>no manual input needed</highlightedText>",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {text}
              </span>
            ),
          }
        ),
      },
      {
        title: t({
          id: "contacts-page.turn-insights",
          defaultMessage: "Turn Insights into Opportunities",
        }),
        description: t({
          id: "contacts-page.turn-insights-desc",
          defaultMessage:
            "Use this data to personalise outreach, qualify leads, and close more deals 📈",
        }),
      },
      {
        title: t({
          id: "contacts-page.upgrade-business-plan-title",
          defaultMessage: "Upgrade to Business Plan to Identify new Customers",
        }),
        description: t({
          id: "contacts-page.upgrade-business-plan-desc",
          defaultMessage:
            "Feature available only for the Business Plan. Visitors Intelligence applies to all new visitors after plan switching",
        }),
      },
    ],
    [t]
  );
  const visitorIntelligenceExampleData = useMemo<ContactInfoData>(
    () => ({
      profile: {},
      visitorIntelligence: {
        phoneNumber: {
          type: "plain",
          value: "+91 1234 567 890",
        },
        personalEmail: { type: "plain", value: "marcus.chen@bigbrothers.com" },
        businessEmail: { type: "notApplicable" },
        company: { type: "plain", value: "Big Brothers" },
        title: { type: "plain", value: "Executive Director" },
        location: { type: "plain", value: "Dallas, TX" },
        linkedin: { type: "link", value: "See profile" },
        revenue: { type: "plain", value: "$5M" },
        industry: { type: "plain", value: "Health and Pharmaceuticals" },
        website: { type: "link", value: "https://www.website.com" },
        companyLinkedin: { type: "link", value: "See profile" },
        confidence: 9,
      },
    }),
    []
  );

  return (
    <>
      <Button
        variant="secondary"
        size={size === "small" ? "sm" : undefined}
        iconEnd={<PricingIcon />}
        onClick={(e) => {
          e.stopPropagation();
          setIsDialogOpen(true);
        }}
      >
        {t({
          id: "contacts-page.visitors-intelligence",
          defaultMessage: "Visitors Intelligence",
        })}
      </Button>
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        className="flex w-full max-w-[1000px] flex-col"
      >
        <div className="border-dark-100 flex h-[60px] shrink-0 items-center justify-between gap-6 border-0 border-b border-solid px-6">
          <div className="flex items-center gap-3">
            <MingcuteIcon />
            <span className="font-secondary font-semibold text-[#383838]">
              {t({
                id: "contacts-page.visitors-intelligence",
                defaultMessage: "Visitors Intelligence",
              })}
            </span>
          </div>
          <PlainButton onClick={() => setIsDialogOpen(false)}>
            <CloseIcon />
          </PlainButton>
        </div>

        <div className="jugl__custom-scrollbar flex flex-col gap-10 overflow-y-auto p-8">
          <div className="relative flex shrink-0 flex-col gap-10 overflow-hidden rounded-3xl bg-[#080410] p-8">
            <Shadow className="pointer-events-none absolute left-0 top-0" />
            <img
              src={platformsImage}
              alt=""
              className="pointer-events-none absolute bottom-0 right-[60px] h-full"
            />
            <span className="font-secondary z-10 text-3xl font-semibold leading-[150%] text-white">
              {t(
                {
                  id: "contacts-page.identify-traffic",
                  defaultMessage: "Identify 70-80%{br} of your website traffic",
                },
                { br: <br /> }
              )}
            </span>
            <Button
              variant="secondary"
              iconEnd={<ArrowIcon />}
              className="z-10 w-max"
              onClick={() => navigateToPage("cxWorkspacePlans")}
            >
              {t({
                id: "contacts-page.upgrade-to-business",
                defaultMessage: "Upgrade to Business",
              })}
            </Button>
          </div>

          <div className="flex shrink-0 flex-col gap-10 px-8">
            <div className="flex flex-col gap-6">
              <span className="font-secondary text-[22px] font-semibold text-[#1A2023]">
                {t({
                  id: "contacts-page.what-you-get",
                  defaultMessage: "What you get?",
                })}
              </span>
              <div>
                {whatYouGetItems.map((item, index) => (
                  <div key={item.title}>
                    <div className="flex flex-col gap-1 py-2">
                      <span className="text-dark font-secondary text-[15px] font-medium">
                        {item.title}
                      </span>
                      <span className="text-dark-700 font-secondary text-sm leading-[160%]">
                        {item.description}
                      </span>
                    </div>
                    {index < whatYouGetItems.length - 1 && (
                      <div className="my-2.5 h-px w-full rounded-[1px] bg-[#F2F2F2]" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex shrink-0 flex-col gap-2">
              <span className="text-dark-600 font-secondary text-sm">
                {t({
                  id: "contacts-page.visitor-intelligence-example",
                  defaultMessage: "✨ Visitor Intelligence profile example:",
                })}
              </span>
              <div className="bg-grey-100 flex flex-col gap-[21px] rounded-[14px] p-6 pt-4">
                <div className="flex items-center gap-4 px-2">
                  <Avatar username="Marcus Chen" size="md" />
                  <span className="text-dark font-secondary text-[17px] font-medium leading-[140%]">
                    Marcus Chen
                  </span>
                </div>
                <div className="flex items-stretch gap-4">
                  <ContactIntelligenceInfoSection
                    sectionId="visitorIntelligence"
                    variant="preview"
                    values={visitorIntelligenceExampleData.visitorIntelligence}
                  />
                </div>
              </div>
            </div>

            <Button
              variant="secondary"
              className="w-max"
              iconEnd={<PricingIcon />}
              onClick={() => navigateToPage("cxWorkspacePlans")}
            >
              {t({
                id: "contacts-page.get-business-plan",
                defaultMessage: "Get Business Plan",
              })}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
